import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/SupabaseAuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Loader2, Mail, Lock, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';

// Logo Component
const Logo = ({ className = "h-24" }) => (
  <img 
    src="/logo-curacheck.png" 
    alt="CuraCheck" 
    className={`${className} object-contain`}
  />
);

// Google Icon
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Rate limiting constants
const MAX_LOGIN_ATTEMPTS = 15; // Blocco dopo 15 tentativi falliti (Test 2.1 Brute Force)
const LOCKOUT_DURATION_MS = 15 * 60 * 1000; // 15 minutes
const MAX_RESET_ATTEMPTS = 5; // Blocco dopo 5 tentativi reset password (Test 2.3)
const RESET_COOLDOWN_MS = 60 * 1000; // 1 minute cooldown between reset batches

// Get/set login attempts from localStorage
const getLoginAttempts = () => {
  const data = localStorage.getItem('login_attempts');
  if (!data) return { count: 0, lastAttempt: 0, lockedUntil: 0 };
  return JSON.parse(data);
};

const setLoginAttempts = (data) => {
  localStorage.setItem('login_attempts', JSON.stringify(data));
};

const getResetAttempts = () => {
  const data = localStorage.getItem('reset_attempts');
  if (!data) return { count: 0, lastAttempt: 0 };
  return JSON.parse(data);
};

const setResetAttempts = (data) => {
  localStorage.setItem('reset_attempts', JSON.stringify(data));
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { signIn, signInWithGoogle, user, loading: authLoading, supabase } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [isPWALogin, setIsPWALogin] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutRemaining, setLockoutRemaining] = useState(0);
  const isSubmitting = useRef(false);

  // Check for lockout on mount
  useEffect(() => {
    const attempts = getLoginAttempts();
    const now = Date.now();
    if (attempts.lockedUntil > now) {
      setIsLocked(true);
      setLockoutRemaining(Math.ceil((attempts.lockedUntil - now) / 1000));
    }
  }, []);

  // Countdown timer for lockout
  useEffect(() => {
    if (lockoutRemaining > 0) {
      const timer = setInterval(() => {
        setLockoutRemaining(prev => {
          if (prev <= 1) {
            setIsLocked(false);
            setLoginAttempts({ count: 0, lastAttempt: 0, lockedUntil: 0 });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [lockoutRemaining]);

  // Check for PWA auto-login email
  useEffect(() => {
    const savedEmail = localStorage.getItem('pwa_auto_login_email');
    if (savedEmail) {
      setEmail(savedEmail);
      setIsPWALogin(true);
      localStorage.removeItem('pwa_auto_login_email');
      toast.info('Inserisci la password per accedere dalla PWA');
    }
  }, []);

  // MIDDLEWARE: Se utente già loggato, redirect alla Dashboard
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting.current || loading) return;
    
    // Check if locked out
    if (isLocked) {
      toast.error(`Account temporaneamente bloccato. Riprova tra ${Math.ceil(lockoutRemaining / 60)} minuti.`);
      return;
    }
    
    // Trim email (auto-sanitize spaces)
    const trimmedEmail = email.trim().toLowerCase();
    
    if (!trimmedEmail || !password) {
      toast.error('Inserisci email e password');
      return;
    }

    isSubmitting.current = true;
    setLoading(true);
    
    try {
      await signIn(trimmedEmail, password);
      
      // Reset attempts on successful login
      setLoginAttempts({ count: 0, lastAttempt: 0, lockedUntil: 0 });
      
      toast.success('Accesso effettuato!');
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      
      // Update failed attempts
      const attempts = getLoginAttempts();
      const newCount = attempts.count + 1;
      const now = Date.now();
      
      if (newCount >= MAX_LOGIN_ATTEMPTS) {
        // Lock the account
        const lockedUntil = now + LOCKOUT_DURATION_MS;
        setLoginAttempts({ count: newCount, lastAttempt: now, lockedUntil });
        setIsLocked(true);
        setLockoutRemaining(Math.ceil(LOCKOUT_DURATION_MS / 1000));
        toast.error(`Troppi tentativi falliti (${MAX_LOGIN_ATTEMPTS}). Account bloccato per 15 minuti.`);
      } else {
        setLoginAttempts({ count: newCount, lastAttempt: now, lockedUntil: 0 });
        // Generic security message - don't reveal if email or password is wrong
        toast.error(`Credenziali non valide. Tentativo ${newCount}/${MAX_LOGIN_ATTEMPTS}`);
      }
    } finally {
      setLoading(false);
      isSubmitting.current = false;
    }
  };

  const handleGoogleLogin = async () => {
    if (isLocked) {
      toast.error('Account temporaneamente bloccato. Riprova più tardi.');
      return;
    }
    
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
      // Il redirect viene gestito da Supabase OAuth
    } catch (error) {
      console.error('Google login error:', error);
      toast.error('Errore durante l\'accesso con Google');
      setGoogleLoading(false);
    }
  };

  // Password Reset Handler with rate limiting
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    const trimmedEmail = resetEmail.trim().toLowerCase();
    
    if (!trimmedEmail) {
      toast.error('Inserisci la tua email');
      return;
    }
    
    // Check rate limiting for reset
    const resetAttempts = getResetAttempts();
    const now = Date.now();
    
    // Reset count if last attempt was more than 1 hour ago
    if (now - resetAttempts.lastAttempt > 60 * 60 * 1000) {
      setResetAttempts({ count: 1, lastAttempt: now });
    } else if (resetAttempts.count >= MAX_RESET_ATTEMPTS) {
      const cooldownEnd = resetAttempts.lastAttempt + RESET_COOLDOWN_MS;
      const waitTime = Math.ceil((cooldownEnd - now) / 1000);
      if (waitTime > 0) {
        toast.error(`Troppi tentativi. Attendi ${waitTime} secondi.`);
        return;
      }
      // Cooldown passed, reset counter
      setResetAttempts({ count: 1, lastAttempt: now });
    } else {
      // Increment counter BEFORE the API call (rate limit per tentativo, non per successo)
      setResetAttempts({ count: resetAttempts.count + 1, lastAttempt: now });
    }
    
    setResetLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(trimmedEmail, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      
      if (error) throw error;
      
      // Always show success message (don't reveal if email exists)
      toast.success('Se l\'email è registrata, riceverai un link di recupero.');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      console.error('Password reset error:', error);
      // Generic message for security
      toast.success('Se l\'email è registrata, riceverai un link di recupero.');
      // Don't close the form on error, let user retry or see rate limit message
    } finally {
      setResetLoading(false);
    }
  };

  // Modal Forgot Password
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex flex-col">
        <header className="p-4 sm:p-6">
          <button 
            onClick={() => setShowForgotPassword(false)}
            className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#4A7C59] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Torna al login</span>
          </button>
        </header>
        
        <main className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <Logo className="h-28 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-[#2C3E50]">
                Recupera Password
              </h1>
              <p className="text-[#64748B] mt-2">
                Ti invieremo un link per reimpostare la password
              </p>
            </div>
            
            <div className="bg-white rounded-3xl shadow-lg p-8">
              <form onSubmit={handlePasswordReset} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-[#2C3E50] mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="w-full h-14 pl-12 pr-4 rounded-xl border-2 border-[#E5E7EB] focus:border-[#4A7C59] focus:outline-none transition-colors"
                      placeholder="La tua email"
                    />
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={resetLoading}
                  className="w-full h-14 bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl font-bold text-lg"
                >
                  {resetLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Invio in corso...
                    </>
                  ) : (
                    'Invia Link di Recupero'
                  )}
                </Button>
              </form>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col">
      {/* Header */}
      <header className="p-4 sm:p-6">
        <Link to="/" className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#4A7C59] transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>Torna alla home</span>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <Logo className="h-28 mx-auto mb-4" />
            <h1 className="text-2xl sm:text-3xl font-bold text-[#2C3E50]">
              Bentornato!
            </h1>
            <p className="text-[#64748B] mt-2">
              Accedi all&apos;Area Famiglia
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-[#2C3E50] mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="la-tua@email.it"
                  className="w-full h-14 pl-12 pr-4 text-lg border-2 border-[#E5E7EB] rounded-xl focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none transition-all"
                  data-testid="login-email"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-[#2C3E50] mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="La tua password"
                  className="w-full h-14 pl-12 pr-12 text-lg border-2 border-[#E5E7EB] rounded-xl focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none transition-all"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#64748B]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Forgot Password */}
            <div className="text-right">
              <button 
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-[#4A7C59] hover:underline"
              >
                Password dimenticata?
              </button>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl font-bold text-lg"
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Accesso in corso...
                </>
              ) : (
                'Accedi'
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-[#E5E7EB]" />
            <span className="text-sm text-[#9CA3AF]">oppure</span>
            <div className="flex-1 h-px bg-[#E5E7EB]" />
          </div>

          {/* Google Login Button */}
          <Button
            type="button"
            onClick={handleGoogleLogin}
            disabled={googleLoading}
            className="w-full h-14 bg-white hover:bg-gray-50 text-[#2C3E50] border-2 border-[#E5E7EB] rounded-xl font-semibold text-lg flex items-center justify-center gap-3"
            data-testid="google-login"
          >
            {googleLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Connessione a Google...
              </>
            ) : (
              <>
                <GoogleIcon />
                Accedi con Google
              </>
            )}
          </Button>

          {/* Register Link */}
          <p className="text-center mt-8 text-[#64748B]">
            Non hai un account?{' '}
            <Link to="/registrazione" className="text-[#4A7C59] font-semibold hover:underline">
              Registrati gratis
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
